import { Injectable } from '@angular/core';
import { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { ChangelogFindInterface } from '@zaoblako/shared/modules/system/dtos/changelog/changelog-find.dto';
import type { ChangelogFormInterface } from '@zaoblako/shared/modules/system/dtos/changelog/changelog-form.dto';
import type { ChangelogPageInterface } from '@zaoblako/shared/modules/system/dtos/changelog/changelog-page.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

/**
 * Provides base CRUD operations with changelog entries.
 *
 * **NOTICE:** _This actions are available only for admin tenant!_
 */
@Injectable({
  providedIn: 'root'
})
export class ChangelogService extends BaseService {
  protected baseUrl = '/basis/system/changelog';

  /**
   * Find many changelog entries with pagination
   *
   * @param options Pagination settings (query parameters)
   *
   * @returns Observable object with changelog entries JSON "page"
   */
  public findManyWithPagination(options: PaginationQueryInterface): Observable<ChangelogPageInterface> {
    return this.http.get<ChangelogPageInterface>(this.baseUrl, {
      params: this.objectToHttpParams(options)
    });
  }

  /**
   * Find one changelog entry by version
   *
   * @param version Application version in "SemVer" format
   *
   * @returns Observable object with found changelog entry
   *
   * @throws "404 Not Found" if entry not found
   */
  public findOneByVersion(version: string): Observable<ChangelogFindInterface> {
    return this.http.get<ChangelogFindInterface>(`${this.baseUrl}/${version}`);
  }

  /**
   * Create or update one changelog
   *
   * @param version Application version in "SemVer" format
   * @param data Entry parameters
   *
   * @returns Observable object with created or updated changelog entry
   */
  public createOrUpdateOne(version: string, data: ChangelogFormInterface): Observable<ChangelogFindInterface> {
    return this.http.put<ChangelogFindInterface>(`${this.baseUrl}/${version}`, data);
  }

  /**
   * Delete one changelog entry by version
   *
   * @param version Application version in "SemVer" format
   *
   * @returns Observable object with deleted changelog entry
   *
   * @throws "404 Not Found" if entry not found
   */
  public deleteOneByVersion(version: string): Observable<ChangelogFindInterface> {
    return this.http.delete<ChangelogFindInterface>(`${this.baseUrl}/${version}`);
  }
}
